import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Plyr from 'plyr-react';
import { X, Pause, Play, VolumeX, Volume2, Maximize, Minimize } from 'lucide-react';
import styles from './css/ViewDocumentary.module.css';
import 'plyr-react/plyr.css';
import { useAudioPlayer } from '../../components/audio/AudioPlayerContext';

const FullVideoModal = ({ video, onClose }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideControlsTimer = useRef(null);
  const { isPlaying: isAudioPlaying, setIsPlaying: setAudioPlaying } = useAudioPlayer();
  
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Netflix-style controls visibility
  useEffect(() => {
    document.body.classList.add(styles.videoModalOpen);
    
    const startHideTimer = () => {
      clearTimeout(hideControlsTimer.current);
      hideControlsTimer.current = setTimeout(() => {
        if (isPlaying) {
          setControlsVisible(false);
        }
      }, 3000);
    };
    
    if (isPlaying) {
      startHideTimer();
    }
    
    const handleMouseMove = () => {
      setControlsVisible(true);
      if (isPlaying) {
        startHideTimer();
      }
    };
    
    const handleTouch = () => {
      setControlsVisible(!controlsVisible);
      if (isPlaying && !controlsVisible) {
        startHideTimer();
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouch);
    
    return () => {
      clearTimeout(hideControlsTimer.current);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouch);
      document.body.classList.remove(styles.videoModalOpen);
    };
  }, [controlsVisible, isPlaying]);

  // Initialize video element
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    
    // Setup event listeners
    const handlePlay = () => {
      setIsPlaying(true);
      if (isAudioPlaying) {
        setAudioPlaying(false);
      }
    };
    
    const handlePause = () => {
      setIsPlaying(false);
    };
    
    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);
    };
    
    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
      
      // Auto-play with delay
      setTimeout(() => {
        try {
          const playPromise = videoElement.play();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              // Simulate Netflix buffering after 1 second of playback
              setTimeout(() => {
                if (videoElement.currentTime < 3) {
                  videoElement.pause();
                  setTimeout(() => {
                    videoElement.play().catch(e => console.warn('Play prevented:', e));
                  }, 1200);
                }
              }, 1000);
            }).catch(error => {
              console.warn('Auto-play was prevented:', error);
            });
          }
        } catch (err) {
          console.warn('Error during auto-play:', err);
        }
      }, 300);
    };
    
    const handleVolumeChange = () => {
      setVolume(videoElement.volume);
      setIsMuted(videoElement.muted);
    };
    
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };
    
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('volumechange', handleVolumeChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      if (videoElement) {
        videoElement.pause();
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('volumechange', handleVolumeChange);
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
      }
    };
  }, [videoRef.current, isAudioPlaying, setAudioPlaying]);

  // Custom play/pause handler
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };
  
  // Custom volume controls
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !video.muted;
  };
  
  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    if (newVolume === 0) {
      video.muted = true;
    } else if (video.muted) {
      video.muted = false;
    }
  };
  
  // Progress bar
  const handleProgressChange = (e) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newTime = parseFloat(e.target.value);
    video.currentTime = newTime;
  };
  
  // Fullscreen handling
  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;
    
    if (!document.fullscreenElement) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };
  
  // Format time for display
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return ReactDOM.createPortal(
    <div 
      className={styles.videoModal} 
      style={{ cursor: controlsVisible ? 'auto' : 'none' }}
    >
      <div 
        ref={containerRef}
        className={styles.modalContent}
      >
        {/* Custom video player */}
        <video
          ref={videoRef}
          className={styles.netflixVideo}
          src={video.video_url}
          preload="auto"
          onClick={togglePlay}
        />
        
        {/* Netflix-style overlay controls */}
        <div className={`${styles.netflixControls} ${!controlsVisible ? styles.hideControls : ''}`}>
          {/* Top controls with close button */}
          <div className={styles.topControls}>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          
          {/* Center play button for mobile */}
          {!isPlaying && (
            <div className={styles.centerControls} onClick={togglePlay}>
              <button className={styles.playButton}>
                <Play size={isMobile ? 50 : 80} />
              </button>
            </div>
          )}
          
          {/* Bottom controls with progress bar and buttons */}
          <div className={styles.bottomControls}>
            {/* Progress bar */}
            <div className={styles.progressContainer}>
              <span className={styles.timeDisplay}>{formatTime(currentTime)}</span>
              <input
                type="range"
                className={styles.progressBar}
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleProgressChange}
              />
              <span className={styles.timeDisplay}>{formatTime(duration)}</span>
            </div>
            
            {/* Control buttons */}
            <div className={styles.controlButtons}>
              <div className={styles.leftControls}>
                <button className={styles.controlButton} onClick={togglePlay}>
                  {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                </button>
                
                {!isMobile && (
                  <div className={styles.volumeControl}>
                    <button className={styles.controlButton} onClick={toggleMute}>
                      {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                    </button>
                    <input
                      type="range"
                      className={styles.volumeSlider}
                      min="0"
                      max="1"
                      step="0.1"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                    />
                  </div>
                )}
              </div>
              
              <div className={styles.rightControls}>
                <button className={styles.controlButton} onClick={toggleFullscreen}>
                  {isFullscreen ? <Minimize size={24} /> : <Maximize size={24} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FullVideoModal;